/**
 * External dependencies
 */
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import {
  Card,
  CardContent,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { Link, useParams } from 'react-router-dom';

/**
 * Internal dependencies
 */
import Breadcrumbs from '@Components/Breadcrumbs';
import NotFound from '@Components/NotFound';
import RichText from '@Components/RichText';
import { CloudinaryImage } from '@art-of-hospitality-solutions-llc/ui-kit/components/cloudinary-image';
import { ScrollArea } from '@art-of-hospitality-solutions-llc/ui-kit/components/scroll-area';
import { useEventBookingEvent } from '../../../hooks/useEventBookingEvent';
import {
  formatPhoneNumberFriendlyDisplay,
  formatZonedDateTime,
} from '@Utils/utils';
import { getSelectionDisplayNames } from '@Utils/toast/purchaseHelpers';
import { ExternalLink } from 'lucide-react';
import { useSecret } from '../../../stores/secretsStore';

function formatDate(dateStr: string | null | undefined) {
  if (!dateStr) return '-';
  return formatZonedDateTime(dateStr, 'MMM d, yyyy');
}

function formatTime(dateStr: string | null | undefined) {
  if (!dateStr) return '-';
  return formatZonedDateTime(dateStr, 'h:mm a');
}

export default function PrivateDiningEventDetails() {
  const { eventId } = useParams();
  const tripleseatBaseUrl = useSecret('TRIPLESEAT_BASE_URL');
  const {
    data: event,
    status,
    isError,
  } = useEventBookingEvent({
    eventId,
    include: 'venue,status,eventType,fish,fishProfile,spaces.featuredAsset',
  });

  if (isError) {
    return <NotFound />;
  }
  if (status === 'pending' || !event) {
    return <div>Loading...</div>;
  }

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'Events', href: '/events' },
          { name: 'Private Dining', href: '/private-dining-events' },
          { name: event.name, href: `/private-dining-events/${eventId}` },
        ]}
      />

      <div className="mt-8 mb-4 flex items-end justify-between">
        <div className="flex items-end">
          <Heading>{event.name}</Heading>
          <p className="mx-2 mb-1 text-sm font-bold text-zinc-500">
            PRIVATE DINING EVENT
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-12 xl:grid-cols-3">
        <div className="flex flex-col space-y-8 xl:col-span-2">
          {/* Details */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <Heading level="h3">Details</Heading>
            </div>
            <Card>
              <CardContent className="relative">
                {event.externalEventId && (
                  <Button
                    variant="outline"
                    size="sm"
                    asChild
                    className="absolute top-0 right-6"
                  >
                    <a
                      href={`${tripleseatBaseUrl}/events/${event.externalEventId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src="/triple-seat-favicon-400x400.png"
                        className="w-4"
                      />
                      View in Tripleseat <ExternalLink />
                    </a>
                  </Button>
                )}
                <div className="flex flex-col space-y-3">
                  <div>
                    <div className="text-sm font-bold text-zinc-500">
                      Event Name
                    </div>
                    {event.name}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-zinc-500">
                      Event Type
                    </div>
                    {event.eventType?.name ?? (
                      <span className="text-zinc-500">No event type</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4 md:grid">
                    <div>
                      <div className="text-sm font-bold text-zinc-500">
                        Guest Count
                      </div>
                      {event.guestCount}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-zinc-500">
                      Package Selection
                    </div>
                    {event.toastOrder ? (
                      getSelectionDisplayNames(
                        typeof event.toastOrder === 'string'
                          ? JSON.parse(event.toastOrder)
                          : event.toastOrder,
                      ).join(', ') || (
                        <span className="text-zinc-500">No selections</span>
                      )
                    ) : (
                      <span className="text-zinc-500">No package selected</span>
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-zinc-500">
                      Additional Notes
                    </div>
                    {event.additionalNotes ? (
                      <div className="rounded-xl bg-zinc-100 p-4">
                        <ScrollArea className="max-h-96 overflow-auto">
                          <RichText
                            content={event.additionalNotes}
                            className="max-w-full!"
                          />
                        </ScrollArea>
                      </div>
                    ) : (
                      <span className="text-zinc-500">No additional notes</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Guest / Fish Profile */}
          <div>
            <div className="mb-2">
              <Heading level="h3">Guest</Heading>
            </div>
            <Card>
              <CardContent>
                {event.fishProfile ? (
                  <div className="flex flex-col space-y-3">
                    <div>
                      <div className="text-sm font-bold text-zinc-500">
                        Name
                      </div>
                      {event.fishProfile.fName} {event.fishProfile.lName}
                    </div>
                    {event.fishProfile.email && (
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Email
                        </div>
                        {event.fishProfile.email}
                      </div>
                    )}
                    {event.fish.phoneNumber && (
                      <div>
                        <div className="text-sm font-bold text-zinc-500">
                          Phone Number
                        </div>
                        {formatPhoneNumberFriendlyDisplay(
                          event.fish.phoneNumber,
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="py-4 text-center text-sm text-zinc-500">
                    No guest profile linked
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="flex flex-col space-y-8">
          {/* Venue, Spaces & Schedule */}
          <div>
            <div className="mb-2">
              <Heading level="h3">Venue & Schedule</Heading>
            </div>
            <Card>
              <CardContent>
                <div className="flex flex-col space-y-4">
                  <div>
                    {event.spaces && event.spaces.length > 0 ? (
                      event.spaces[0].featuredAsset && (
                        <div className="mb-4 overflow-hidden rounded-md border border-zinc-200 bg-zinc-100">
                          <CloudinaryImage
                            publicId={
                              event.spaces[0].featuredAsset.cloudinaryId
                            }
                            mode="original"
                            className="h-40 w-full object-cover"
                          />
                        </div>
                      )
                    ) : (
                      <span className="text-zinc-500">No space assigned</span>
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-zinc-500">Venue</div>
                    {event.venue ? (
                      <Link
                        to={`/venues/${event.venue.id}`}
                        className="hover:underline"
                      >
                        {event.venue.name}
                      </Link>
                    ) : (
                      <span className="text-zinc-500">No venue assigned</span>
                    )}
                  </div>
                  <div>
                    {event.spaces && event.spaces.length > 0 ? (
                      <div className="flex flex-col space-y-3">
                        {event.spaces.map((space) => (
                          <div key={space.id}>
                            <div className="text-sm font-bold text-zinc-500">
                              Space
                            </div>
                            <Link
                              to={`/spaces/${space.id}`}
                              className="hover:underline"
                            >
                              {space.name}
                            </Link>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-zinc-500">No space assigned</span>
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-zinc-500">
                      Schedule
                    </div>
                    <div className="mt-1 mb-2 text-base font-semibold">
                      {formatDate(event.startDate)}
                    </div>
                    <div className="mb-4">
                      <div className="mt-3 flex text-center text-xs">
                        <div className="w-16 shrink-0">
                          <div className="font-bold text-zinc-500">Setup</div>
                          <div>{formatTime(event.setupStartDate)}</div>
                        </div>
                        <div className="flex-1" />
                        <div className="w-16 shrink-0">
                          <div className="font-bold text-zinc-500">Start</div>
                          <div>{formatTime(event.startDate)}</div>
                        </div>
                        <div className="flex-2" />
                        <div className="w-16 shrink-0">
                          <div className="font-bold text-zinc-500">End</div>
                          <div>{formatTime(event.endDate)}</div>
                        </div>
                        <div className="flex-1" />
                        <div className="w-16 shrink-0">
                          <div className="font-bold text-zinc-500">
                            Breakdown
                          </div>
                          <div>{formatTime(event.breakdownEndDate)}</div>
                        </div>
                      </div>
                      <div className="mt-2 flex items-center">
                        <div className="flex w-16 shrink-0 justify-center">
                          <div className="h-2.5 w-2.5 rounded-full bg-zinc-400" />
                        </div>
                        <div className="h-0.5 flex-1 bg-zinc-300" />
                        <div className="flex w-16 shrink-0 justify-center">
                          <div className="h-2.5 w-2.5 rounded-full bg-zinc-600" />
                        </div>
                        <div className="h-0.5 flex-2 bg-zinc-300" />
                        <div className="flex w-16 shrink-0 justify-center">
                          <div className="h-2.5 w-2.5 rounded-full bg-zinc-600" />
                        </div>
                        <div className="h-0.5 flex-1 bg-zinc-300" />
                        <div className="flex w-16 shrink-0 justify-center">
                          <div className="h-2.5 w-2.5 rounded-full bg-zinc-400" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
